import type { PresentationRef } from '../protocol/wire';
import type {
  CapabilityV2, FileRef, Interactions, Layouts, Manifest, ManifestV2, Presentation, PresentationV2, SceneFile, TargetsFile, Templates, Theme, ThemeV2,
} from './types.ts';
import { validateInteractions, validateLayouts, validateManifest, validateTemplates, validateTheme, type Issue } from './validate.ts';
import { validateScene, validateSceneArtwork, validateTargets } from './validate2.ts';
import { validateArt, validateArtSlots, validateAtlases, validateFaces } from '../faces/validate.ts';
import type { ArtFile, AtlasesFile, FacesFile } from '../faces/types.ts';
import { frameIndex } from '../faces/art.ts';
import { withArtRelease } from '../art/assets.ts';
import { validateBoards, validateBoardThreeReferences } from '../boards/validate.ts';
import type { BoardVisuals } from '../boards/types.ts';

/** Capabilities this client implements (presentation contracts v1 section 2 and v2 section 3). */
export const CLIENT_CAPABILITIES: CapabilityV2[] = ['cards', 'board-graph', 'board-regions', 'allocation', 'ordering', 'images', 'scene', 'direct-play', 'motion', 'materials', 'faces', 'atlases', 'hosted-art', 'complex-board', 'records', 'pixi-board', 'edge-pieces', 'rich-text', 'catalog-guide', 'seat-ring', 'three-board', 'three-helpers'];

export type LoadedPresentation = Presentation | PresentationV2;

export type PresentationResult =
  | { status: 'none' }
  | { status: 'ready'; presentation: LoadedPresentation }
  | { status: 'fallback'; reason: string };

export const isV2 = (p: LoadedPresentation | null): p is PresentationV2 => !!p && 'format' in p && p.format === 2;

function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1]!;
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`${url} returned ${response.status}`);
  return response.json();
}

/**
 * Loads the presentation the host announced in `joined`. Any failure falls back to the
 * generic layout with a reason; a presentation package can never block play.
 */
export async function loadPresentation(ref: PresentationRef | null, gameId: string | null): Promise<PresentationResult> {
  if (!ref) return { status: 'none' };
  try {
    const manifest = await fetchJson(ref.manifestUrl) as Manifest | ManifestV2;
    const manifestIssues = validateManifest(manifest).filter(i => i.severity === 'error');
    if (manifestIssues.length) return { status: 'fallback', reason: describe(manifestIssues) };
    if (gameId && manifest.compatible.gameId !== gameId) return { status: 'fallback', reason: 'The presentation is for a different game.' };
    const missing = (manifest.requires as CapabilityV2[]).filter(c => !CLIENT_CAPABILITIES.includes(c));
    if (missing.length) return { status: 'fallback', reason: `This client lacks: ${missing.join(', ')}.` };
    const base = ref.manifestUrl.slice(0, ref.manifestUrl.lastIndexOf('/') + 1);
    const file = (f: FileRef | undefined) => (f ? fetchJson(base + basename(f.path)) : Promise.resolve(undefined));
    const assetIds = new Set(manifest.assets.map(a => a.id));
    const assetUrls = new Map(manifest.assets.map(a => [a.id, `/assets/${a.sha256}`]));
    if (manifest.formatVersion === 'presentation-2') {
      const result = await loadV2(manifest, file, assetIds, assetUrls, base);
      if (result.status === 'ready' && isV2(result.presentation) && ref.module && manifest.module) {
        const expected = new URL(base + basename(manifest.module.path), window.location.href);
        const announced = new URL(ref.module.url, window.location.href);
        if (announced.origin === window.location.origin && announced.href === expected.href && ref.module.api === manifest.module.api && ref.module.sha256 === manifest.module.sha256)
          result.presentation.module = ref.module;
      }
      return result;
    }
    const [layouts, templates, interactions, theme] = await Promise.all([
      file(manifest.files.layouts), file(manifest.files.templates), file(manifest.files.interactions), file(manifest.files.theme),
    ]);
    const issues = [
      ...validateLayouts(layouts), ...validateTemplates(templates, theme as Theme, assetIds),
      ...validateInteractions(interactions), ...validateTheme(theme, assetIds),
    ].filter(i => i.severity === 'error');
    if (issues.length) return { status: 'fallback', reason: describe(issues) };
    return {
      status: 'ready',
      presentation: { manifest, layouts: layouts as Layouts, templates: templates as Templates, interactions: interactions as Interactions, theme: theme as Theme, assetUrls },
    };
  } catch (error) {
    return { status: 'fallback', reason: `The presentation could not be loaded (${(error as Error).message}).` };
  }
}

async function loadV2(manifest: ManifestV2, file: (f: FileRef | undefined) => Promise<unknown>, assetIds: Set<string>, assetUrls: Map<string, string>, base = ''): Promise<PresentationResult> {
  const f = manifest.files;
  const [scene, targets, templates, interactions, theme, layouts, faces, atlases, art, boards] = await Promise.all([
    file(f.scene), file(f.targets), file(f.templates), file(f.interactions), file(f.theme), file(f.layouts), file(f.faces), file(f.atlases), file(f.art), file(f.boards),
  ]);
  const sheets = new Map(manifest.assets.map(a => [a.id, { width: a.width, height: a.height }]));
  const themeV2 = theme as ThemeV2;
  let presentation: PresentationV2 = {
    format: 2, manifest, scene: scene as SceneFile, targets: (targets as TargetsFile | undefined) ?? { targets: [] },
    templates: (templates as Templates | undefined) ?? { templates: [] }, interactions: (interactions as Interactions | undefined) ?? {},
    theme: themeV2, layouts: (layouts as Layouts | undefined) ?? null, faces: (faces as FacesFile | undefined) ?? null,
    atlases: (atlases as AtlasesFile | undefined) ?? null, art: (art as ArtFile | undefined) ?? null, assetUrls, boards: boards as BoardVisuals | undefined,
    ...(manifest.models?.length ? { models: new Map(manifest.models.map(m => [m.id, { url: base + basename(m.path), sha256: m.sha256, bytes: m.bytes }])) } : {}),
    ...(manifest.standees?.length ? {
      standees: new Map(manifest.standees.map(s => [s.id, {
        url: base + basename(s.path), sha256: s.sha256, bytes: s.bytes, front: s.front,
        artBounds: { min: [s.artBounds[0], s.artBounds[1]] as [number, number], max: [s.artBounds[2], s.artBounds[3]] as [number, number] },
        height: s.height, mounting: s.mounting, facing: s.facing, edge: s.edge,
      }])),
    } : {}),
  };
  if (manifest.artRelease) {
    try {
      presentation = await withArtRelease(presentation);
      for (const a of presentation.artRelease!.assets) { assetIds.add(a.id); const image = a.images.at(-1)!; sheets.set(a.id, { width: image.width, height: image.height }); }
    } catch { presentation.artNotice = 'Artwork is unavailable; using placeholders.'; }
  }
  const sceneIssues = validateScene(scene);
  const atlasIssues = atlases !== undefined ? validateAtlases(atlases, sheets, !!manifest.artRelease && !presentation.artRelease) : [];
  const iconFrames = new Set(!atlasIssues.some(i => i.severity === 'error') ? frameIndex((atlases as AtlasesFile | undefined) ?? null).keys() : []);
  const issues = [
    ...sceneIssues,
    ...(!sceneIssues.some(i => i.severity === 'error') ? validateSceneArtwork(scene as SceneFile, manifest.artRelease && !presentation.artRelease ? undefined : assetIds) : []), ...validateTheme(theme, manifest.artRelease ? undefined : assetIds, true, iconFrames),
    ...(boards !== undefined ? validateBoards(boards) : []),
    ...(boards !== undefined && !validateBoards(boards).some(i => i.severity === 'error') ? validateBoardThreeReferences(boards as BoardVisuals, manifest.requires, new Set((manifest.models ?? []).map(m => m.id)), new Set((manifest.standees ?? []).map(s => s.id))) : []),
    ...(targets !== undefined ? validateTargets(targets) : []),
    ...(templates !== undefined ? validateTemplates(templates, themeV2, manifest.artRelease && !presentation.artRelease ? undefined : assetIds) : []),
    ...(interactions !== undefined ? validateInteractions(interactions) : []),
    ...(layouts !== undefined ? validateLayouts(layouts) : []),
    ...(faces !== undefined ? validateFaces(faces, themeV2, assetIds, manifest.requires) : []),
    ...atlasIssues,
    // Missing frames are warnings here: the slot falls back and play continues.
    ...(art !== undefined ? validateArt(art, (atlases as AtlasesFile | undefined) ?? null, false) : []),
    ...(faces !== undefined && art !== undefined ? validateArtSlots(faces as FacesFile, art as ArtFile) : []),
  ].filter(i => i.severity === 'error');
  if (issues.length) return { status: 'fallback', reason: describe(issues) };
  return { status: 'ready', presentation };
}

function describe(issues: Issue[]): string {
  const first = issues[0]!;
  return `The presentation is invalid: ${first.file} ${first.path}: ${first.message}${issues.length > 1 ? ` (+${issues.length - 1} more)` : ''}`;
}
